import { HelpTopic } from '@core/models/domain/help.domain';

/**
 * The School App help, in one place.
 *
 * This is the single source of truth: the Help & FAQ page lists every topic, and a contextual
 * Help button shows one of them. Nothing here is duplicated in a template, so a correction made
 * here reaches both.
 *
 * The content describes what the app actually does today. When a page changes, its topic changes
 * with it.
 */
export const HELP_TOPICS: HelpTopic[] = [
  {
    key: 'dashboard',
    title: 'Dashboard',
    icon: 'home',
    route: '/dashboards/home',
    keywords: ['home', 'overview', 'today', 'metrics', 'summary'],
    whatIsIt:
      "The Dashboard is the first page you land on. It answers one question: what does the school look like today?",
    whatDoesItDo: [
      'Shows four headline figures: device enrolment, pupils awaiting a device, restrictions scheduled today, and enrolled devices that are no longer checking in.',
      "Draws today's timeline, so you can see when restrictions run across the school day and where the gaps are.",
      'Breaks enrolment down by group, lowest coverage first, and shows the mix of enrolled device types.',
      "Lists today's restrictions, where you can stop or remove one before it runs.",
      'Lists the restrictions a parent has called off today, with the reason they gave.',
      'Lists the pupils who still have no device registered, so you can chase their enrolment.',
    ],
    faqs: [
      {
        question: 'What does the device enrolment figure mean?',
        answer:
          'It is the share of your pupils who have at least one device registered in LOQT. A pupil with no device cannot receive school restrictions, so this figure is the best single measure of how much of the school is covered.',
      },
      {
        question: 'Why does a pupil appear under "Awaiting a device"?',
        answer:
          'Nobody has registered a device for that pupil yet. Registration happens in the LOQT family app, by the parent, so the school cannot complete it for them. Open the pupil to see their details and chase the parent.',
        keywords: ['pending', 'no device', 'parent'],
      },
      {
        question: 'What are the colours on the timeline?',
        answer:
          'They are the four states a restriction can be in on the day: upcoming, in progress, completed, and cancelled. The timeline window fits the restrictions of the day, and the arrows walk it across the rest of the day.',
        keywords: ['legend', 'upcoming', 'in progress', 'completed', 'cancelled'],
      },
      {
        question: 'Can I stop a restriction from the Dashboard?',
        answer:
          "Yes. Today's restrictions table carries the same actions as the Restrictions page, so you can stop one that is running or remove one that has not started. You need an administrator account to do it.",
        keywords: ['stop', 'delete', 'cancel'],
      },
      {
        question: 'Why does the Dashboard show parent cancellations?',
        answer:
          'A parent can call off a school restriction for their own child from the family app. The school is not asked to approve it, so the Dashboard lists the cancellations of the day with the reason the parent gave, and the school is also emailed and notified in the app.',
        keywords: ['parent', 'cancelled', 'reason'],
      },
      {
        question: 'How fresh are these figures?',
        answer:
          'They are read when you open the page. Re-clicking Dashboard in the menu reloads them in the background, leaving what is on screen visible while the new figures arrive.',
        keywords: ['refresh', 'reload', 'stale'],
      },
    ],
  },
  {
    key: 'notifications',
    title: 'Notifications',
    icon: 'notifications',
    route: '/dashboards/notifications',
    keywords: ['inbox', 'bell', 'alerts', 'notices'],
    whatIsIt:
      'Notifications are the notices the school has received: a parent cancelling a school restriction, a parent asking to link their child to one of your pupils, a parent asking to remove a pupil account, and the end of an MIS synchronisation.',
    whatDoesItDo: [
      'Puts unread notices behind the bell in the top bar, with a count.',
      'Keeps the full history on the Notifications page, grouped into collapsible periods from Today to Older.',
      'Opens the page that answers a notice when you click it, and highlights the item there.',
      'Lets you mark everything as read, or clear your own inbox.',
    ],
    faqs: [
      {
        question: 'Does clearing my notifications affect my colleagues?',
        answer:
          'No. Read state and clearing are your own. A colleague looking at the same school keeps their own unread count and their own history.',
        keywords: ['read', 'shared', 'per user'],
      },
      {
        question: 'Does clearing a notice cancel the request behind it?',
        answer:
          'No. A notice is only a pointer. Clearing it leaves the parent request, the cancellation or the pupil untouched, and the request stays on its own page until you decide it.',
        keywords: ['delete', 'clear all', 'request'],
      },
      {
        question: 'Why is a period missing from the list?',
        answer:
          'Periods whose calendar range is empty are left out. On a Monday there is no "This Week" yet, and in January there is no "This Year", because those ranges hold no days.',
        keywords: ['today', 'yesterday', 'this week', 'older', 'period'],
      },
      {
        question: 'How quickly do new notices appear?',
        answer:
          'The app checks for new notices about once a minute while you are signed in, and re-reads the list whenever you open the Notifications page.',
        keywords: ['polling', 'refresh', 'delay'],
      },
      {
        question: 'Do notices go to everyone at the school?',
        answer:
          'Notices about parent requests are for administrators. Email is separate: LOQT emails school notices to the staff listed under Settings, which you choose yourself.',
        keywords: ['admin', 'email', 'recipients'],
      },
    ],
  },
  {
    key: 'restrictions',
    title: 'Restrictions',
    icon: 'hourglass_empty',
    route: '/restriction/list',
    keywords: ['tasks', 'lessons', 'blocking', 'schedule', 'timetable'],
    whatIsIt:
      "A restriction is a window of time during which the pupils' devices are locked down by LOQT. You give it a name, a time window, and the classes or groups it applies to.",
    whatDoesItDo: [
      'Shows the restrictions of a week, one column per day, or one day at a time with full details.',
      'Finds a restriction by its name or by the name of a group it applies to.',
      'Creates a restriction that runs once, or one that repeats every week on the days you pick.',
      'Assigns a restriction to one or more classes or groups. Every pupil in them is covered.',
      'Edits a restriction that has not started, with a choice of how far the change reaches in a repeating series.',
      'Stops a restriction that is running, or cancels one that has not started, for a single day or for the whole series.',
      'Marks a restriction that a calendar entry is suspending, so you can see why it is not running.',
    ],
    faqs: [
      {
        question: 'One-time or recurring: which should I pick?',
        answer:
          'Pick one-time for a one-off such as an exam. Pick recurring for anything that repeats every week, then choose the days. The Weekdays and Every day shortcuts fill the days in for you.',
        keywords: ['repeat', 'weekly', 'once', 'series'],
      },
      {
        question: 'Why can I not save my restriction?',
        answer:
          'The form refuses a start time in the past, an end time that is not after the start, and a window shorter than five minutes. A recurring restriction also needs at least one day selected. The reason is shown under the field it applies to.',
        keywords: ['error', 'validation', 'five minutes', 'past'],
      },
      {
        question: 'I edited a repeating restriction. Which occurrences changed?',
        answer:
          'You are asked. Only this occurrence turns the chosen day into a separate one-time restriction and leaves the rest of the series alone. From the selected date restarts the series from that day with the new settings. All occurrences applies the change to the whole series, keeping its original start date.',
        keywords: ['scope', 'occurrence', 'series', 'modify'],
      },
      {
        question: 'How do I cancel a repeating restriction?',
        answer:
          'Use the delete action and choose how far the cancellation reaches: the selected date only, every occurrence from the start, or every occurrence from a date you pick. Earlier occurrences stay visible.',
        keywords: ['cancel', 'delete', 'stop', 'from date'],
      },
      {
        question: 'Can I edit a restriction that is already running?',
        answer:
          'No. Once a restriction is in progress or finished, the edit action is gone. A running restriction can still be stopped, which ends it immediately on the pupils devices.',
        keywords: ['in progress', 'stop', 'edit', 'running'],
      },
      {
        question: 'A restriction says it is suspended. Why?',
        answer:
          'A school calendar entry covers that day for those pupils, so the restriction does not run. The card names the entry. The restriction is not deleted and resumes by itself once the entry is over.',
        keywords: ['suspended', 'holiday', 'closure', 'calendar', 'trip'],
      },
      {
        question: 'Why did a pupil not get a restriction their class did?',
        answer:
          'Three things exclude a pupil: they have no device registered, they are marked exempt on the Users page, or their own timetable or a calendar entry says they are not in school that day.',
        keywords: ['exempt', 'part-time', 'missing', 'device'],
      },
      {
        question: 'What do the statuses mean?',
        answer:
          'Upcoming has not started yet. In progress is running now. Completed ran to its end. Cancelled was called off by the school. Restriction cancelled was called off by a parent for their own child. Skipped did not run at all, for instance because of a calendar entry.',
        keywords: ['status', 'upcoming', 'skipped', 'completed'],
      },
    ],
  },
  {
    key: 'devices',
    title: 'Devices',
    icon: 'add_to_home_screen',
    route: '/device/list',
    keywords: ['phones', 'tablets', 'enrolment', 'hardware'],
    whatIsIt:
      "Devices is the directory of every device registered against a pupil at your school, and the list of pupils who still have none.",
    whatDoesItDo: [
      'Lists registered devices with their owner, the groups the owner belongs to, the device type and model, the enrolment date and the current status.',
      'Searches across owner, group, type, model and status.',
      'Swaps the table for the pupils who have no device registered yet.',
      'Flags an enrolled device that has stopped checking in, with the date it went quiet.',
    ],
    faqs: [
      {
        question: 'Can the school register a device for a pupil?',
        answer:
          'No. A device is paired and enrolled from the LOQT family app, by the parent, on the device itself. The school sees the result here.',
        keywords: ['add device', 'register', 'pair', 'parent'],
      },
      {
        question: 'What do the device statuses mean?',
        answer:
          'Enrolled means the device finished enrolment and can receive restrictions. Pending enrolment means it was paired but enrolment is not finished, so no restriction reaches it yet. Unenrolled means it has been removed. Inactive means the owner is no longer an active pupil at your school.',
        keywords: ['status', 'enrolled', 'pending', 'unenrolled', 'inactive'],
      },
      {
        question: 'A device is tagged "Not checking in". What should I do?',
        answer:
          "It is enrolled but has not contacted LOQT for some time, so restrictions may not be reaching it. The tooltip gives the date of the last check-in. Ask the family to open the LOQT app on the device. The tag clears itself on the next check-in, and the parents are alerted once it has been quiet long enough.",
        keywords: ['stale', 'offline', 'not checking in', 'quiet'],
      },
      {
        question: 'Why has a device disappeared from the list?',
        answer:
          'Devices the school has disabled, and devices whose owner has left the school, are hidden from the directory.',
        keywords: ['missing', 'removed', 'disabled', 'left'],
      },
      {
        question: 'Why does a pupil have more than one device?',
        answer:
          'A pupil can have several devices registered. Restrictions apply to all of them, so a pupil cannot step around a restriction by picking up their other phone or tablet.',
        keywords: ['multiple', 'two devices', 'tablet'],
      },
    ],
  },
  {
    key: 'users',
    title: 'Users',
    icon: 'person',
    route: '/people/users',
    keywords: ['pupils', 'staff', 'teachers', 'roster', 'people'],
    whatIsIt:
      'Users is your roster in LOQT: the pupils you manage, and the staff who can sign in to this workspace.',
    whatDoesItDo: [
      'Lists everyone with their groups, role and status, and searches and filters by role and by pupil status.',
      'Adds a pupil, with their date of birth, gender, unique pupil number and parent or guardian details, or adds a member of staff.',
      'Opens a user to edit their details, or removes them.',
      'Marks a pupil exempt from LOQT, or removes that exemption.',
      'Exports everyone matching the current filters to a CSV file.',
    ],
    faqs: [
      {
        question: 'What do the pupil statuses mean?',
        answer:
          'Active means the pupil is fully enrolled with at least one registered device. Pending means no device has been registered yet, so the parent still has to do it. Exempt means the pupil is excused from LOQT and school restrictions are never applied to their devices.',
        keywords: ['status', 'active', 'pending', 'exempt'],
      },
      {
        question: 'What does marking a pupil exempt do?',
        answer:
          'The pupil stays on your roll and in their groups, but no school restriction is ever applied to their devices, including one that is running at the moment you exempt them. You give a reason, which is shown next to their status. Removing the exemption puts them back under school restrictions.',
        keywords: ['exempt', 'excuse', 'reason', 'opt out'],
      },
      {
        question: 'Who can add or delete a user?',
        answer:
          'Only a school administrator. Staff without the administrator role can open the lists and read a user, because those pages double as read-only views, but the write actions are refused.',
        keywords: ['permission', 'admin', 'teacher', 'role'],
      },
      {
        question: 'Do I have to add pupils by hand?',
        answer:
          'Only if your school does not use the MIS integration. When it is connected, pupils are read from your MIS through Wonde and placed in year groups, classes or both, depending on your sync mode, so the roster does not have to be typed twice.',
        keywords: ['import', 'wonde', 'mis', 'bulk'],
      },
      {
        question: 'A pupil appears twice. What should I do?',
        answer:
          'Two records for one child usually come from a parent linking a child that your MIS also sent, or from a typo in a school code. Check the Review mismatches tab of MIS Integration and the Parent Requests page: linking the right pupil there is what merges the two paths, rather than deleting a record.',
        keywords: ['duplicate', 'twice', 'merge', 'same pupil'],
      },
      {
        question: 'What happens to a pupil I delete?',
        answer:
          'They stop appearing in lists and their devices drop out of the directory. If the pupil is linked to a family in the LOQT family app, deletion requests from the parent side come to you as a parent request instead, so the school decides.',
        keywords: ['delete', 'remove', 'deactivate'],
      },
    ],
  },
  {
    key: 'groups',
    title: 'Groups',
    icon: 'group',
    route: '/people/groups',
    keywords: ['classes', 'sets', 'year groups', 'form'],
    whatIsIt:
      'A group is a named set of pupils: a class, a set, a form or anything else you want to restrict together. Restrictions are assigned to groups, never to a pupil one at a time.',
    whatDoesItDo: [
      'Lists your groups with their member count and status, and searches them by name. Year groups shows your year groups and the groups your school made, and Classes shows the classes imported from your MIS.',
      'Creates a group with a name and description, then adds members from the user list.',
      'Opens a group to rename it, or to add and remove members.',
    ],
    faqs: [
      {
        question: 'Can a pupil be in more than one group?',
        answer:
          'Yes, and most are. A pupil in several groups is covered by every restriction assigned to any of them.',
      },
      {
        question: 'What happens to restrictions when I change a group?',
        answer:
          'Restrictions follow the group, not a fixed list of pupils. A pupil you add is covered by the next run of that group restrictions, and a pupil you remove stops being covered.',
        keywords: ['members', 'add', 'remove', 'restriction'],
      },
      {
        question: 'Are my MIS classes groups?',
        answer:
          'It depends on the sync mode chosen on the MIS Integration page. With classes, each MIS class arrives here as a group, under Classes, and every synchronisation keeps its members up to date. With year groups, the synchronisation keeps one group per year, either one it created or one of yours it adopted, and moves pupils into the right one. Groups you create yourself are yours to maintain, and a synchronisation never changes them.',
        keywords: ['wonde', 'mis', 'classes', 'sync', 'year groups', 'sync mode'],
      },
      {
        question: 'Can I delete a group that a restriction uses?',
        answer:
          'Check the Restrictions page first. A restriction with no group left covers nobody, and the card shows "No groups" so you can spot it.',
        keywords: ['delete', 'empty', 'no groups'],
      },
    ],
  },
  {
    key: 'school-profile',
    title: 'School Profile',
    icon: 'school',
    route: '/settings/admin/school-account',
    keywords: ['school details', 'address', 'logo', 'photo', 'contact'],
    whatIsIt:
      "The School Profile holds your school's details in LOQT: its name, photo, contact information and address.",
    whatDoesItDo: [
      'Shows the school details, and lets an administrator switch to edit mode to change them.',
      'Sets the school photo shown beside your school name across the workspace.',
      'Keeps the contact phone numbers and the postal address that identify the school to parents.',
    ],
    faqs: [
      {
        question: 'Where do these details show up?',
        answer:
          'They identify the school to parents in the LOQT family app and on the messages LOQT sends them, which is why it is worth keeping them current.',
      },
      {
        question: 'What photo can I upload?',
        answer:
          'A PNG or JPG up to 5MB. It is resized before it is saved. Without a photo, the workspace shows your school initials instead.',
        keywords: ['logo', 'image', 'upload', 'initials'],
      },
      {
        question: 'Why can I not edit anything?',
        answer:
          'Only a school administrator can change the school profile. Other staff see the details read-only.',
        keywords: ['permission', 'admin', 'read only'],
      },
    ],
  },
  {
    key: 'calendar',
    title: 'Calendar',
    icon: 'event_busy',
    route: '/school-calendar/overview',
    keywords: ['holidays', 'closure', 'inset', 'half term', 'trip', 'exceptions', 'suspend'],
    whatIsIt:
      'The School Calendar is where you record the periods when school restrictions should not apply: holidays, closures, trips and anything else that takes pupils out of the normal school day.',
    whatDoesItDo: [
      'Shows a month grid of your entries next to the full list, with search and filters by who they affect and by status.',
      'Adds an entry with a type, a name, a first and last day, and optionally only part of a day.',
      'Chooses who it affects: the whole school, year groups, classes or groups, or pupils you select.',
      'Starts an entry immediately when you need to, which also removes restrictions that are running right now from the affected pupils devices.',
      'Withdraws an entry that is no longer needed, which puts the restrictions back.',
    ],
    faqs: [
      {
        question: 'Does an entry delete my restrictions?',
        answer:
          'No. Restrictions are never deleted by a calendar entry. They are suspended for the period it covers and resume by themselves afterwards. Family restrictions set by parents are not affected at all.',
        keywords: ['delete', 'suspend', 'resume', 'family'],
      },
      {
        question: 'What types of entry are there?',
        answer:
          'School holiday, half term, bank holiday, INSET day, school closure, emergency closure, partial closure, late start, early finish, school trip, residential trip, work experience, study leave, exam leave, authorised absence, and Other for anything else. The type is a label. What changes behaviour is the period and who it covers.',
        keywords: ['type', 'inset', 'bank holiday', 'study leave', 'exam'],
      },
      {
        question: 'How do I close the school part way through a day?',
        answer:
          'Turn on "Only part of the day" and give the times. Suspended from applies on the first day and Suspended until on the last. A restriction that is interrupted this way ends for the day rather than splitting in two.',
        keywords: ['partial', 'half day', 'early finish', 'late start'],
      },
      {
        question: 'What does "Start immediately" do?',
        answer:
          'It takes effect the moment you save, rather than waiting for the start of the day. Restrictions running right now are removed from the affected pupils devices. Use it for a snow day or an unplanned closure.',
        keywords: ['emergency', 'now', 'snow day', 'urgent'],
      },
      {
        question: 'Can one entry cover only some pupils?',
        answer:
          'Yes. Choose year groups, classes or groups, or pick pupils individually. A school trip for one class only suspends restrictions for that class, and the rest of the school carries on as normal.',
        keywords: ['scope', 'year group', 'class', 'selected pupils', 'trip'],
      },
      {
        question: 'I withdrew an entry by mistake. What now?',
        answer:
          'Withdrawn entries stay in the list behind the "Show withdrawn" toggle, so you can see what was there. Add the entry again for the period you still need.',
        keywords: ['cancel', 'withdraw', 'undo', 'restore'],
      },
      {
        question: 'Do parents see calendar entries?',
        answer:
          'Parents do not see your calendar. They see the effect: on a suspended day no school restriction arrives on their child device.',
        keywords: ['parents', 'family app', 'visible'],
      },
    ],
  },
  {
    key: 'part-time-pupils',
    title: 'Part-time pupils',
    icon: 'schedule',
    route: '/school-calendar/part-time-pupils',
    keywords: ['attendance', 'timetable', 'part time', 'flexi'],
    whatIsIt:
      'Part-time pupils is where you record a pupil who does not attend the full week. It is a standing fact about the pupil, unlike a calendar entry, which is about a date.',
    whatDoesItDo: [
      'Lists the pupils on an individual timetable, the days they attend, and the period the timetable applies to.',
      'Puts a pupil on an individual timetable, chosen from a searchable pupil list.',
      'Edits a timetable, including the date it starts to apply and the date it stops.',
    ],
    faqs: [
      {
        question: 'What happens on a day a pupil does not attend?',
        answer:
          'No school restriction is applied to their device that day. The restriction still runs for the rest of their class.',
        keywords: ['absent', 'not in school', 'restriction'],
      },
      {
        question: 'Should I use this or a calendar entry?',
        answer:
          'Use a timetable for a pattern that repeats every week for one pupil, such as Mondays and Tuesdays only. Use a calendar entry for a specific date or period, however many pupils it covers.',
        keywords: ['difference', 'calendar', 'when to use'],
      },
      {
        question: 'How do I stop a timetable?',
        answer:
          'Give it an "Applies until" date. After that date the pupil follows the normal school week again, and the restrictions of their groups reach them on every day.',
        keywords: ['end', 'stop', 'full time', 'until'],
      },
      {
        question: 'Why is a pupil not in the list I can choose from?',
        answer:
          'The picker shows the pupils of your school, narrowed as you type. If someone is genuinely missing, they are not on your roster yet: add them under Users, or synchronise from your MIS.',
        keywords: ['missing', 'search', 'picker'],
      },
    ],
  },
  {
    key: 'mis-integration',
    title: 'MIS Integration',
    icon: 'sync',
    route: '/settings/admin/mis-integration',
    keywords: ['wonde', 'sims', 'import', 'sync', 'roster', 'mismatch', 'sync mode', 'year groups', 'classes'],
    whatIsIt:
      'MIS Integration connects LOQT to your school management information system through Wonde, so your school, pupils, year groups and classes do not have to be entered twice.',
    whatDoesItDo: [
      'Shows how far the connection has got: not linked, waiting for approval, ready to synchronise, and synchronised, partially synchronised or failed.',
      'Sets how a synchronisation places pupils into groups: year groups only, classes only, or year groups and classes.',
      'Runs a synchronisation that pulls your school and its pupils from your MIS, with the year groups and classes your sync mode uses. The run carries on if you leave the page.',
      'Checks every change before writing anything, and stops with nothing changed when a restriction would lose too many of its pupils.',
      'Reports what the run did in separate blocks for pupils, groups, classes and problems.',
      'Lists on the Review mismatches tab the pupils it could not match with certainty and the pupils whose year it could not work out, so you decide each one.',
    ],
    faqs: [
      {
        question: 'Why is the Sync button unavailable?',
        answer:
          'Four things enable it: you are a school administrator, your school is connected in Wonde, the connection has reached a state where a synchronisation is allowed, and a sync mode is saved under Sync settings. The progress steps at the top of the page show how far the connection has got. The button also stays unavailable while a synchronisation is running.',
        keywords: ['disabled', 'greyed out', 'cannot sync', 'permission', 'mode'],
      },
      {
        question: 'Which sync mode should I choose?',
        answer:
          'Year groups only keeps one group per year, such as Year 7, worked out from your MIS classes, and creates no class groups. Classes only makes one group per MIS class and no year groups. Year groups and classes does both. Choose the groups your restrictions are aimed at. No synchronisation can run until a mode is saved, and groups your school created itself are never changed by a synchronisation, whatever the mode.',
        keywords: ['mode', 'year groups only', 'classes only', 'mixed', 'settings', 'first sync'],
      },
      {
        question: 'Can I change the sync mode later?',
        answer:
          'Yes. An administrator chooses another mode and presses Save. The next synchronisation then deactivates the groups the new mode no longer uses. Year groups only deactivates the MIS class groups. Classes only deactivates the year groups the synchronisation created, and hands back to your school any group of yours it had adopted as a year group. The change is refused while an active restriction uses one of those groups. Change that restriction first.',
        keywords: ['change mode', 'switch', 'deactivate', 'refused'],
      },
      {
        question: 'How does the synchronisation set up year groups?',
        answer:
          'For each year found in your MIS classes, it uses the year group it already keeps. Otherwise, if exactly one of your own active groups is named like that year, such as "Year 7", "Yr7" or "Y7", it adopts it: the group keeps its members and restrictions, is renamed "Year 7" and is kept up to date by your MIS from then on. With no such group, it creates "Year 7". When two or more groups look like the same year, it sets up nothing for that year and lists them under "Groups not used as year groups". Keep only one of them and synchronise again.',
        keywords: ['adopted', 'created', 'renamed', 'year group', 'conflict', 'groups not used'],
      },
      {
        question: 'How long does a synchronisation take?',
        answer:
          'It depends on the size of your school and can take several minutes. You do not have to wait on the page: the synchronisation carries on if you leave, and coming back to the page shows its progress or its result. A notice also appears in your Notifications when it ends.',
        keywords: ['duration', 'leave', 'running', 'progress'],
      },
      {
        question: 'Why did the synchronisation stop with nothing changed?',
        answer:
          'The synchronisation works out every change before writing any. It stops when an active restriction would lose more than 10% of its pupils, and lists each such restriction with how many pupils would leave it. Check the figures. If the change is expected, for example at the start of a new school year, press Sync anyway. Sync anyway only covers the restrictions listed, so a new one over the limit stops the run again. It also stops when your sync mode would deactivate a group an active restriction uses. Sync anyway is not offered then: change the restriction or the mode first.',
        keywords: ['stopped', 'refused', 'nothing was changed', 'sync anyway', 'restriction', 'guard'],
      },
      {
        question: 'What does "Needs review" mean?',
        answer:
          'Your MIS sent a pupil that could not be matched to exactly one pupil at your school, either because several look alike, because none is close enough, or because the matching pupil is already linked to a different MIS record. Nothing has been created or changed for them. They wait on the Review mismatches tab until you decide. The tab and its count appear only while pupils are waiting.',
        keywords: ['mismatch', 'review', 'unmatched', 'duplicate', 'tab'],
      },
      {
        question: 'What does "Year unknown" mean?',
        answer:
          'In a mode with year groups, the pupil was synchronised but your MIS does not say clearly which year they are in, for example because their classes are in different years or none of them has a year. They kept their current groups. They are listed on the Review mismatches tab under "Pupils whose year could not be worked out". Assign groups chooses their year group and school groups, while their MIS classes stay with your MIS. Skip leaves them as they are, and later synchronisations do not ask again unless their MIS classes change. The report counts every such pupil of the run, including those you have already settled.',
        keywords: ['year unknown', 'assign groups', 'ambiguous', 'year could not be worked out'],
      },
      {
        question: 'What do the review actions do?',
        answer:
          'Press Update on the line of the pupil already in LOQT that the MIS record is. The panel shows each field that differs: untick any your school wants to keep until the next synchronisation, then choose Update from MIS. Classes and MIS identifiers are always updated. When the pupil looks like a different child, the page warns you and leaves name and date of birth unticked. Add as new pupil creates a pupil from the MIS record. Skip ignores the record, and later synchronisations keep skipping it until it changes in your MIS.',
        keywords: ['link', 'skip', 'create', 'update', 'decision', 'different child'],
      },
      {
        question: 'Does a synchronisation overwrite pupils I added by hand?',
        answer:
          'Only where a pupil is matched to an MIS record. Pupils your MIS does not send are left alone, and an unmatched MIS record never quietly overwrites anyone: it waits for your decision on the review tab. Groups your school made are never changed. A synchronisation only manages the year groups and classes of your sync mode.',
        keywords: ['overwrite', 'manual', 'safe', 'my groups'],
      },
      {
        question: 'Does synchronising delete anything?',
        answer:
          'It never deletes a pupil. It does move pupils between the year groups and classes it manages as your MIS changes, and the report shows how many were added to or removed from groups. Changing the sync mode deactivates the groups the new mode no longer uses. A pupil your MIS no longer sends keeps their groups. Removing a pupil stays a decision you make on the Users page, or one that comes to you as a parent request.',
        keywords: ['delete', 'remove', 'leavers', 'deactivated', 'memberships'],
      },
      {
        question: 'What happens to a running restriction when a sync moves pupils?',
        answer:
          'A pupil taken out of a group while one of its restrictions is running stays under that restriction until it ends, and the change applies from its next run. The restriction may no longer show in the pupil\'s app meanwhile, but their phone is released on time when it ends.',
        keywords: ['running', 'in progress', 'locked', 'moved', 'group change'],
      },
      {
        question: 'A run reports failures. What should I do?',
        answer:
          'The Problems block lists the records that could not be synchronised. Run the synchronisation again once the underlying record is corrected in your MIS. A failed pupil is not half-created: it is simply not there yet.',
        keywords: ['failed', 'error', 'retry', 'problems'],
      },
    ],
  },
  {
    key: 'parent-requests',
    title: 'Parent Requests',
    icon: 'pending_actions',
    route: '/settings/admin/parent-requests',
    keywords: ['link', 'deactivation', 'approve', 'decline', 'family'],
    whatIsIt:
      'Parent Requests holds the decisions parents have asked the school to make: linking a child in the family app to one of your pupils, and removing a pupil LOQT account.',
    whatDoesItDo: [
      'Lists pupil link requests with the details the parent gave and the pupils at your school that could be the child.',
      'Links the request to the right pupil, creates a new pupil when none of them is the child, or rejects the request.',
      'Lists account deactivation requests with the reason the parent gave.',
      'Approves a deactivation, declines it, or keeps the pupil on your roll but exempt from LOQT.',
    ],
    faqs: [
      {
        question: 'Why do these requests come to the school at all?',
        answer:
          'Because the child is one of your pupils. Nothing changes for them until you decide, so a parent cannot link to the wrong child or remove a pupil account on their own.',
        keywords: ['why', 'approval', 'safeguard'],
      },
      {
        question: 'How do I know which pupil the request means?',
        answer:
          'The request shows the name and date of birth the parent gave, next to the pupils at your school that could match, with how close each match is. Select one, then link it.',
        keywords: ['match', 'candidates', 'date of birth', 'choose'],
      },
      {
        question: 'What if none of the suggested pupils is the child?',
        answer:
          'Use "No match, create new pupil". The child joins your roster from the details the parent gave. Use Reject when the request should not have reached your school at all.',
        keywords: ['new pupil', 'reject', 'none', 'wrong school'],
      },
      {
        question: 'What are my options on a deactivation request?',
        answer:
          'Approve removes the pupil LOQT account. Decline leaves everything as it is. Exempt keeps the pupil on your roll but stops school restrictions ever applying to their devices, which is often the answer when a family wants out without leaving the school.',
        keywords: ['approve', 'decline', 'exempt', 'delete'],
      },
      {
        question: 'Who can decide these requests?',
        answer:
          'Only a school administrator. Other staff can see the page but the decision buttons are not offered to them.',
        keywords: ['permission', 'admin', 'who'],
      },
    ],
  },
  {
    key: 'settings',
    title: 'Settings',
    icon: 'tune',
    route: '/settings/admin/system',
    keywords: ['recipients', 'email', 'preferences', 'school settings'],
    whatIsIt:
      'Settings holds the choices that apply to the whole school rather than to your own account. Everyone at the school works from them.',
    whatDoesItDo: [
      'Chooses which staff receive the school notices LOQT sends by email, such as a parent cancelling a school restriction for their child.',
      'Adds a recipient from the active staff accounts, or removes one.',
    ],
    faqs: [
      {
        question: 'Who can be a notification recipient?',
        answer:
          'Any member of staff with an active user account. Staff who leave or are deactivated stop receiving the notices automatically, so the list does not have to be cleaned up by hand.',
        keywords: ['staff', 'add', 'leavers'],
      },
      {
        question: 'What if I remove everybody?',
        answer:
          'Nobody at the school receives these emails. Keep at least one recipient. The in-app Notifications page is unaffected either way.',
        keywords: ['empty', 'nobody', 'none'],
      },
      {
        question: 'Where are my own preferences?',
        answer:
          'Your own account lives under My Profile, reached from your name in the top right. Settings is only for choices shared by the whole school.',
        keywords: ['profile', 'my account', 'password'],
      },
      {
        question: 'Why can I not change anything here?',
        answer:
          'Only a school administrator can edit school settings. Other staff see them read-only.',
        keywords: ['permission', 'admin', 'read only'],
      },
    ],
  },
];
